/**
 * Service detail screen (DNKADM-0055 U2 + U3) — one `v2_service` with its four
 * **assignment editors** (categories / locations / eligible staff / eligible resources),
 * the **empty-pool warnings**, and the **variations** panel (handoff §5.5). Routed at
 * `/services/:serviceId` under `RequireAdmin`. Mirrors `ResourceDetail` (DNKADM-0054).
 *
 * The header shows the lifecycle **status** chip (Draft/Active/Inactive, DNKADM-0140 —
 * replaced the old isActive chip) and, for a `DRAFT`, a **Publish** button that takes the
 * service live (the one-way DRAFT→ACTIVE move; there is no unpublish), plus the
 * **Edit details** button (DNKADM-0286) that opens the existing {@link ServiceDialog}
 * against the loaded record — the detail page is the single place to view AND edit the
 * base fields; the list's quick-edit trigger is retired. A save refetches the record.
 *
 * **Assignments (U2).** Each editor is a multi-select round-trip over a fail-closed
 * pick-list: categories (0052), locations (0051 — ≥1 enforced), eligible staff (0053),
 * eligible resources (0054). Locations require at least one (a service must be offered
 * somewhere). Saving any editor recomputes the empty-pool warnings.
 *
 * **Empty-pool warning (§4.7 / §5.5).** When a required dimension
 * (`MEMBER_SELECTS`/`ROUND_ROBIN`) has no eligible entity covering an offered location,
 * the screen surfaces a config-time warning — the booking-time mirror of
 * `SERVICE_HAS_NO_ASSIGNABLE_STAFF/RESOURCE` (the backend would yield no availability).
 *
 * **Variations (U3).** The {@link VariationsPanel} lists/edits variations (required
 * `creditCost`, exactly one default) and hosts the per-staff price exceptions.
 */
import { useCallback, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { usePickList } from '@/hooks/usePickList';
import { useToast } from '@/components/useToast';
import {
  getService,
  getServiceCategories,
  getServiceLocations,
  getServiceResources,
  getServiceStaff,
  publishService,
  setServiceCategories,
  setServiceLocations,
  setServiceResources,
  setServiceStaff,
  type V2Service,
} from '@/api/services';
import { serverMessage } from '@/api/serverMessage';
import { getServiceCategoryOptions } from '@/api/serviceCategories';
import { getStaffOptions, getStaffLocations } from '@/api/staff';
import { getResourceOptions, getResourceLocations } from '@/api/resources';
import { listLocations } from '@/api/locations';
import { listCreditBuckets, type CreditBucket } from '@/api/creditBuckets';
import { PickListError } from '@/api/picklists';
import { AssignmentEditor, type IdNameOption } from './AssignmentEditor';
import { VariationsPanel } from './VariationsPanel';
import { ServiceDialog } from './ServiceDialog';
import { ServiceSettingsPanel } from './ServiceSettingsPanel';
import { ScreenError } from '@/components/ScreenError';

/** Display name for a service (its name, falling back to the id). */
function serviceName(service: V2Service): string {
  return service.name.length > 0 ? service.name : service.id;
}

/**
 * Load ALL owned locations (every status) as fail-closed `{ id, name, isActive }`
 * options (no shared location pick-list exists, so this local wrapper mirrors the
 * others' fail-closed contract — a non-array success or a thrown error becomes a
 * `PickListError`). Loading every status (DNKADM-0134) keeps an already-assigned but
 * since-deactivated location visible in the editor; `AssignmentEditor` tags it inline.
 */
async function getLocationOptions(): Promise<IdNameOption[]> {
  try {
    const locs = await listLocations({ status: 'all' });
    return locs.map((l) => ({
      id: l.id,
      name: l.name.length > 0 ? l.name : l.id,
      isActive: l.isActive,
    }));
  } catch (cause) {
    throw new PickListError('locations', cause);
  }
}

/**
 * Compute the empty-pool warnings for the required dimensions. For each required
 * dimension (staff/resource ≠ NONE) and each offered location, at least one assigned
 * entity must also be assigned to that location; otherwise the location is uncovered.
 * Returns a per-dimension message when there is no assigned entity at all or an
 * offered location is uncovered.
 */
async function computeEmptyPoolWarnings(
  service: V2Service,
  locationNameById: Map<string, string>,
): Promise<string[]> {
  const warnings: string[] = [];
  const offeredLocationIds = await getServiceLocations(service.id);

  async function checkDimension(
    label: string,
    assignedIds: string[],
    getEntityLocations: (id: string) => Promise<string[]>,
  ): Promise<void> {
    if (assignedIds.length === 0) {
      warnings.push(`No eligible ${label} are assigned, but this service selects ${label}.`);
      return;
    }
    if (offeredLocationIds.length === 0) return; // location warning handled elsewhere
    // Map each offered location to whether any assigned entity covers it.
    const entityLocations = await Promise.all(
      assignedIds.map((id) => getEntityLocations(id).catch(() => [] as string[])),
    );
    const covered = new Set<string>();
    for (const locs of entityLocations) for (const l of locs) covered.add(l);
    const uncovered = offeredLocationIds.filter((l) => !covered.has(l));
    if (uncovered.length > 0) {
      const names = uncovered.map((l) => locationNameById.get(l) ?? l).join(', ');
      warnings.push(`No eligible ${label} at: ${names}. Bookings there will find no availability.`);
    }
  }

  if (service.staffAssignment !== 'NONE') {
    const staffIds = await getServiceStaff(service.id);
    await checkDimension('staff', staffIds, getStaffLocations);
  }
  if (service.resourceAssignment !== 'NONE') {
    const resourceIds = await getServiceResources(service.id);
    await checkDimension('resources', resourceIds, getResourceLocations);
  }
  return warnings;
}

export function ServiceDetail() {
  const { serviceId = '' } = useParams<{ serviceId: string }>();
  const showToast = useToast();

  const service = useResource(
    useCallback(() => getService(serviceId), [serviceId]),
    [serviceId],
  );

  // The active locations (for the empty-pool warning's location-name resolution).
  const locations = useResource(
    useCallback(() => listLocations({ status: 'active' }), []),
    [],
  );
  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locations.data ?? []) map.set(l.id, l.name.length > 0 ? l.name : l.id);
    return map;
  }, [locations.data]);

  // Credit-bucket id → name for the Settings panel (DNKADM-0295, Operator-approved
  // scope addition — mirrors ServicesScreen's identical bucketNameById pattern). A
  // load failure degrades to an empty map (never blocks the detail page); the panel
  // renders an unresolvable id as `—`, not the raw id and not a fabricated name.
  const buckets = useResource(
    useCallback(() => listCreditBuckets({ status: 'all' }), []),
    [],
  );
  const bucketNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of (buckets.data ?? []) as CreditBucket[]) {
      map.set(b.id, b.name.length > 0 ? b.name : b.id);
    }
    return map;
  }, [buckets.data]);

  const serviceData = service.data;
  // Empty-pool warnings recompute when the service or location names change, and on the
  // `warningsKey` bump an assignment save triggers.
  const warnings = useResource(
    useCallback(
      () =>
        serviceData
          ? computeEmptyPoolWarnings(serviceData, locationNameById)
          : Promise.resolve<string[]>([]),
      [serviceData, locationNameById],
    ),
    [serviceData, locationNameById],
  );
  // The service's ASSIGNED (eligible) staff ids — the candidate set for per-staff price
  // exceptions (only an assigned staffer may carry an override, §5.5). A load failure
  // degrades to no eligible staff (the editor shows "assign staff first").
  const assignedStaff = useResource(
    useCallback(() => getServiceStaff(serviceId), [serviceId]),
    [serviceId],
  );

  const recomputeWarnings = warnings.refetch;
  const refetchAssignedStaff = assignedStaff.refetch;
  const onAssignmentsSaved = useCallback(() => recomputeWarnings(), [recomputeWarnings]);
  // A staff-assignment save changes both the warnings AND the eligible set the price
  // editor offers.
  const onStaffAssignmentsSaved = useCallback(() => {
    recomputeWarnings();
    refetchAssignedStaff();
  }, [recomputeWarnings, refetchAssignedStaff]);

  // Publish a Draft service live (DNKADM-0140) — the one-way DRAFT→ACTIVE move; refetch
  // surfaces the new Active status + drops the Publish affordance. No unpublish (taking a
  // live service down still uses Deactivate on the list).
  const handlePublish = useCallback(async () => {
    if (!service.data) return;
    try {
      await publishService(service.data.id);
      service.refetch();
      showToast('Service published', 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? 'Couldn’t publish the service.', 'error');
    }
  }, [service, showToast]);

  // Fail-closed pick-lists for the four assignment editors.
  const categoryList = usePickList(useCallback(() => getServiceCategoryOptions(), []), []);
  const locationList = usePickList(useCallback(() => getLocationOptions(), []), []);
  const staffList = usePickList(
    useCallback(() => getStaffOptions({ includeInactive: true }), []),
    [],
  );
  const resourceList = usePickList(
    useCallback(() => getResourceOptions({ includeInactive: true }), []),
    [],
  );

  // Resolve the assigned staff ids to `{ id, name }` against the roster — the per-staff
  // price editor offers ONLY the service's eligible staff, never the whole roster.
  const staffOptions = useMemo<IdNameOption[]>(() => {
    const roster = staffList.items ?? [];
    const assignedIds = new Set(assignedStaff.data ?? []);
    return roster.filter((s) => assignedIds.has(s.id));
  }, [staffList.items, assignedStaff.data]);

  // Base-field editing in-context (DNKADM-0286): the same dialog the list's Add uses,
  // seeded with the loaded record; a save refetches the record (the empty-pool
  // warnings recompute off the refreshed `service.data`).
  const [editOpen, setEditOpen] = useState(false);

  return (
    <Box>
      <MuiLink
        component={RouterLink}
        to="/services"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, mb: 2 }}
      >
        <Icon name="arrow_back" sx={{ fontSize: 18 }} />
        All services
      </MuiLink>

      {service.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={120}
          role="status"
          aria-label="Loading service"
          sx={{ borderRadius: 3, maxWidth: 560 }}
        />
      )}

      {service.status === 'error' && (
        <ScreenError
          error={service.error}
          what="the service"
          onRetry={service.refetch}
          sx={{ maxWidth: 560 }}
          notFound={
            <Alert severity="warning" sx={{ maxWidth: 560 }}>
              That service wasn’t found. It may have been removed.{' '}
              <MuiLink component={RouterLink} to="/services">
                Back to services
              </MuiLink>
              .
            </Alert>
          }
        />
      )}

      {service.status === 'ready' && service.data && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: service.data.description ? 0.5 : 3 }}>
            <Typography variant="h6" component="h1">
              {serviceName(service.data)}
            </Typography>
            {/* Lifecycle status chip (DNKADM-0140) — replaced the old isActive chip.
                Inlined here rather than sharing ServicesTable's helper (this file doesn't
                import from it; one small three-way mapping isn't worth a cross-file dep). */}
            <Chip
              size="small"
              label={
                service.data.status === 'DRAFT'
                  ? 'Draft'
                  : service.data.status === 'INACTIVE'
                    ? 'Inactive'
                    : 'Active'
              }
              color={
                service.data.status === 'DRAFT'
                  ? 'warning'
                  : service.data.status === 'INACTIVE'
                    ? 'default'
                    : 'success'
              }
              variant={service.data.status === 'ACTIVE' ? 'filled' : 'outlined'}
            />
            {service.data.status === 'DRAFT' && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Icon name="publish" />}
                onClick={() => void handlePublish()}
              >
                Publish
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon name="edit" />}
              onClick={() => setEditOpen(true)}
            >
              Edit details
            </Button>
          </Stack>
          {service.data.description && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              {service.data.description}
            </Typography>
          )}

          {/* ---- Empty-pool warnings ---- */}
          {warnings.status === 'ready' && warnings.data && warnings.data.length > 0 && (
            <Stack spacing={1} sx={{ mb: 3, maxWidth: 720 }}>
              {warnings.data.map((message, i) => (
                <Alert key={i} severity="warning" icon={<Icon name="warning" />}>
                  {message}
                </Alert>
              ))}
            </Stack>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* ---- Assignments (U2) + Settings (DNKADM-0295) two-column layout ----
              Single column below `lg` (Assignments first — it's the editable one),
              side-by-side at `lg`+ (Assignments left, Settings right). `lg` (1200px)
              satisfies the ≥1024/1280 desktop-additive guardrail without a custom
              breakpoint. */}
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={4} sx={{ mb: 4, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Assignments
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Tag categories, choose the locations this service is offered at, and assign the
                eligible staff and resources that can fulfil it.
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Categories
                  </Typography>
                  <AssignmentEditor
                    serviceId={serviceId}
                    label="Categories"
                    pickListLabel="categories"
                    list={categoryList}
                    getIds={getServiceCategories}
                    setIds={setServiceCategories}
                    showToast={showToast}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Locations
                  </Typography>
                  <AssignmentEditor
                    serviceId={serviceId}
                    label="Locations"
                    pickListLabel="locations"
                    list={locationList}
                    getIds={getServiceLocations}
                    setIds={setServiceLocations}
                    minOne
                    showToast={showToast}
                    onSaved={onAssignmentsSaved}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Eligible staff
                  </Typography>
                  {/* DNKADM-0395 U2 — a service that SELECTS staff must not lose its whole
                      eligible set in one click. A service whose staff selection is "Not used"
                      (NONE) has no pool to protect, so it saves empty unprompted. */}
                  <AssignmentEditor
                    serviceId={serviceId}
                    label="Eligible staff"
                    pickListLabel="staff"
                    list={staffList}
                    getIds={getServiceStaff}
                    setIds={setServiceStaff}
                    confirmEmpty={
                      service.data.staffAssignment === 'NONE'
                        ? undefined
                        : {
                            title: `Remove all coaches from ${serviceName(service.data)}?`,
                            body: 'Bookings for this service will have no one to assign.',
                          }
                    }
                    showToast={showToast}
                    onSaved={onStaffAssignmentsSaved}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Eligible resources
                  </Typography>
                  <AssignmentEditor
                    serviceId={serviceId}
                    label="Eligible resources"
                    pickListLabel="resources"
                    list={resourceList}
                    getIds={getServiceResources}
                    setIds={setServiceResources}
                    confirmEmpty={
                      service.data.resourceAssignment === 'NONE'
                        ? undefined
                        : {
                            title: `Remove all resources from ${serviceName(service.data)}?`,
                            body: 'Bookings for this service will have nothing to assign.',
                          }
                    }
                    showToast={showToast}
                    onSaved={onAssignmentsSaved}
                  />
                </Box>
              </Stack>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <ServiceSettingsPanel
                service={service.data}
                bucketNameById={bucketNameById}
                onEdit={() => setEditOpen(true)}
              />
            </Box>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* ---- Variations (U3) ---- */}
          <VariationsPanel
            serviceId={serviceId}
            staffOptions={staffOptions}
            variationDisplay={service.data.variationDisplay}
          />

          <ServiceDialog
            open={editOpen}
            service={service.data}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              setEditOpen(false);
              service.refetch();
            }}
            showToast={showToast}
          />
        </>
      )}
    </Box>
  );
}
